import { useRef } from 'react';

import { Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { DatePickerInput, TimeInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import type { ContextModalProps } from '@mantine/modals';

import { IconCalendar, IconClock } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../UI/Button/Button';

import { useUpdateInteractionMutation } from '../../../hooks/useUpdateInteractionMutation.hook';

type UpdateTenderDeadlineModalProps = {
  tenderId: number;
  initialDeadline?: string;
};

type FormValues = {
  deadline: Date | null;
  time: string | null;
};

export function UpdateTenderDeadlineModal({
  context,
  id,
  innerProps,
}: ContextModalProps<UpdateTenderDeadlineModalProps>) {
  const { tenderId, initialDeadline } = innerProps;
  const { t } = useTranslation();
  const updateInteractionMutation = useUpdateInteractionMutation();
  const ref = useRef<HTMLInputElement>(null);

  const form = useForm<FormValues>({
    initialValues: {
      deadline: null,
      time: null,
    },
    validate: {
      deadline: (value: Date | null) => !value,
      time: (value: string | null) => !value,
    },
  });

  const onSubmit = async (values: FormValues) => {
    if (form.validate().hasErrors || !values.deadline) {
      return;
    }

    const [hours, minutes] = values.time?.split(':') || ['0', '0'];
    const date = new Date(values.deadline);
    date.setHours(parseInt(hours), parseInt(minutes), 0);
    updateInteractionMutation.mutate({
      tenderId,
      payload: {
        userUpdatedResponseDeadline: date,
      },
    });
    context.closeModal(id);
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack p="04" spacing="04">
        <Stack spacing="02">
          <Text fw={500} variant="md" c="gray.9">
            {t('modals.updateTenderDeadlineModal.title')}
          </Text>

          <DatePickerInput
            placeholder={t(
              'modals.updateTenderDeadlineModal.deadlineInputPlaceholder',
            )}
            w="100%"
            defaultDate={
              initialDeadline ? new Date(initialDeadline) : new Date()
            }
            popoverProps={{
              withinPortal: true,
            }}
            locale="fr-FR"
            valueFormat="DD/MM/YYYY"
            radius="md"
            rightSection={
              <ThemeIcon size="sm" color="transparent" c="primary.7">
                <IconCalendar size={16} stroke={2} />
              </ThemeIcon>
            }
            styles={theme => ({
              input: {
                border: `1px solid ${theme.colors.gray[2]}`,
              },
            })}
            {...form.getInputProps('deadline')}
            mx="auto"
            maw={400}
          />

          <TimeInput
            ref={ref}
            w="100%"
            placeholder={t(
              'modals.updateTenderDeadlineModal.timeInputPlaceholder',
            )}
            {...form.getInputProps('time')}
            onClick={() => ref.current?.showPicker()}
            mx="auto"
            radius="md"
            rightSection={
              <ThemeIcon size="sm" color="transparent" c="primary.7">
                <IconClock size={16} stroke={2} />
              </ThemeIcon>
            }
            c="red.9"
            styles={theme => ({
              input: {
                border: `1px solid ${theme.colors.gray[2]}`,
              },
            })}
            maw={400}
          />
        </Stack>
        <Group noWrap position="right" spacing="02">
          <Button
            variant="subtle"
            color="gray"
            onClick={() => {
              context.closeModal(id);
            }}
          >
            {t('cancel')}
          </Button>
          <Button size="md" type="submit" disabled={!form.isValid()}>
            {t('validate')}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
